import { Button, Divider } from '@vrplatform/components';
import { useApiQuery, useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import { formatCurrency, sum } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  Modal,
  Text,
  Title,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useBankAccountId } from '../../views/reconciliation/bank-accounts/view/useBankAccountId';
import { BaseCsvImportModal } from './BaseCsvImportModal';
import type { TEMPLATES } from './_templates';
import type { CsvImportModalConnection } from './_types';

type Props = {
  uniqueRef:
    | 'expense'
    | 'payout'
    | 'bankRecords'
    | 'contacts'
    | 'deposits'
    | 'advancedDeposits';
  opened: boolean;
  closeModal: () => void;
  connection: CsvImportModalConnection | undefined;
  onSuccess?: () => void | Promise<void>;
};

export const CsvImportModal = (props: Props) => {
  if (props.uniqueRef === 'payout' || props.uniqueRef === 'deposits') {
    return <Deposits {...props} />;
  }

  if (props.uniqueRef === 'expense') {
    return <Expenses {...props} />;
  }

  if (props.uniqueRef === 'contacts') {
    return <Contacts {...props} />;
  }

  if (props.uniqueRef === 'bankRecords') {
    return <BankRecords {...props} />;
  }

  if (props.uniqueRef === 'advancedDeposits') {
    return <AdvancedDeposits {...props} />;
  }

  return null;
};

const Expenses = ({
  opened,
  closeModal,
  connection,
  uniqueRef,
  onSuccess,
}: Props) => {
  return (
    <BaseCsvImportModal
      closeModal={closeModal}
      opened={opened}
      invalidateQueryKeys={['expenses', 'accounts']}
      invalidateApiQueryGroups={['expenses', 'accounts']}
      successMessage={{
        title: 'Expenses imported',
        message: 'Your expenses have been imported successfully',
      }}
      modal={{
        title: 'Import Expenses',
        appIcon: connection?.app.logo ?? undefined,
        uniqueRef,
        showDateFormatInput: true,
        documentationUrl:
          connection?.app.documentationUrl ||
          'https://support.vrplatform.app/en/articles/10487094-import-expenses-via-csv',
        googleSheetId: '1MV7r1v1qu_w0W_m8kiyuhHTqGLLOFtU2_Ju7zugLpVQ',
      }}
      connectionId={connection?.id}
      onSuccess={onSuccess}
    />
  );
};

const Deposits = ({
  opened,
  closeModal,
  connection,
  uniqueRef,
  onSuccess,
}: Props) => {
  const isDefaultCsvImport = connection?.app.id === 'csvImport';

  return (
    <BaseCsvImportModal
      connectionId={connection?.id}
      closeModal={closeModal}
      opened={opened}
      invalidateQueryKeys={['payments', 'deposits']}
      invalidateApiQueryGroups={['deposits', 'accounts']}
      successMessage={{
        title: 'Deposits imported',
        message: 'Your deposits have been imported successfully',
      }}
      modal={{
        title:
          connection && !isDefaultCsvImport
            ? `Import from ${connection.app.name}`
            : 'Import Deposits',
        appIcon: connection?.app.logo ?? undefined,
        uniqueRef,
        showDateFormatInput: true,
        googleSheetId: isDefaultCsvImport
          ? '16s24eQopobl4ATtUjT1HeY7Sj0gZScYD2ZstSo9j2Hs'
          : undefined,
        documentationUrl: connection?.app.documentationUrl,
      }}
      onSuccess={onSuccess}
    />
  );
};

const Contacts = ({
  opened,
  closeModal,
  connection,
  uniqueRef,
  onSuccess,
}: Props) => {
  return (
    <BaseCsvImportModal
      closeModal={closeModal}
      opened={opened}
      modal={{
        title: 'Import Contacts',
        uniqueRef,
        showDateFormatInput: false,
        appIcon: connection?.app.logo ?? undefined,
        documentationUrl:
          connection?.app.documentationUrl ||
          'https://support.vrplatform.app/en/articles/11473331-import-contacts-via-csv',
        googleSheetId: '1-7XcdrHrLM-LQvj1o33MwGGS7U9lk9_Obh3tj9Zh-r0',
      }}
      successMessage={{
        title: 'Contacts imported',
        message: 'Your contacts have been imported successfully.',
      }}
      invalidateQueryKeys={['contacts']}
      invalidateApiQueryGroups={['expenses']}
      connectionId={connection?.id}
      onSuccess={onSuccess}
    />
  );
};

const AdvancedDeposits = ({
  opened,
  closeModal,
  connection,
  uniqueRef,
  onSuccess,
}: Props) => {
  return (
    <BaseCsvImportModal
      closeModal={closeModal}
      opened={opened}
      invalidateQueryKeys={['deposits']}
      invalidateApiQueryGroups={['deposits', 'accounts']}
      successMessage={{
        title: 'Advance deposits imported',
        message: 'Your advance deposits have been imported successfully.',
      }}
      modal={{
        title: 'Import advance deposits',
        uniqueRef,
        showDateFormatInput: false,
        documentationUrl: connection?.app.documentationUrl,
        description: (
          <>
            Upload a CSV file to add payments and refunds to VRPlatform. Your
            CSV should include header and follow specified structure.
          </>
        ),
        submitLabel: 'Import advance deposits',
        templateLinkLabel: 'Download a CSV Template',
        googleSheetId: '1tXej-Fp3uWDuUUULo8JkWMCTvdfy1yLgMUFAfUT-YJY',
      }}
      connectionId={connection?.id}
      onSuccess={onSuccess}
    />
  );
};

const BANK_RECORD_MODAL_SEARCH_PARAM = 'modal';
const BANK_RECORD_MODAL_SEARCH_PARAM_VALUE = 'bankRecords';

export function useGotoBankRecordCsvModal() {
  const goto = useNavigate();
  const [searchParams] = useSearchParams();

  return (accountId: string) => {
    const current = window.location.pathname;

    const basePath = `/reconciliation/account/${accountId}`;

    if (current.includes(basePath)) {
      searchParams.set(
        BANK_RECORD_MODAL_SEARCH_PARAM,
        BANK_RECORD_MODAL_SEARCH_PARAM_VALUE
      );
      goto(`${basePath}?${searchParams.toString()}`);
    } else
      goto(
        `/reconciliation/account/${accountId}?${BANK_RECORD_MODAL_SEARCH_PARAM}=${BANK_RECORD_MODAL_SEARCH_PARAM_VALUE}`
      );
  };
}

export function useBankImportCsvForcedParam() {
  return useQueryState(BANK_RECORD_MODAL_SEARCH_PARAM, parseAsString);
}

export function isBankCsvImportForcedOpened() {
  const [forced] = useBankImportCsvForcedParam();
  return forced === BANK_RECORD_MODAL_SEARCH_PARAM_VALUE;
}

const BankRecords = ({
  opened,
  closeModal,
  connection,
  uniqueRef,
  onSuccess,
}: Props) => {
  const accountId = useBankAccountId();

  const [, setForced] = useBankImportCsvForcedParam();
  const isForced = isBankCsvImportForcedOpened();

  const [success, setSuccess] = useState<{
    uploadedRecords: typeof TEMPLATES.bankRecords;
  } | null>(null);

  return (
    <>
      <BaseCsvImportModal
        connectionId={connection?.id}
        closeModal={() => {
          closeModal();
          setForced(null);
        }}
        opened={isForced || opened}
        modal={{
          uniqueRef,
          appIcon: connection?.app.logo ?? undefined,
          title: 'Import Bank Records',
          showDateFormatInput: true,
          googleSheetId: '1_JjFBkW-NQJfoXnJlISbCuWXja8TXvDPkvO9BdTVvfE',
        }}
        successMessage={{
          title: 'Bank records imported',
          message: 'Your bank records have been imported successfully.',
        }}
        invalidateQueryKeys={['bankRecords', 'accounts']}
        invalidateApiQueryGroups={['accounts']}
        accountId={accountId}
        onSuccess={(data) => {
          if (data) {
            setSuccess({
              uploadedRecords: data as typeof TEMPLATES.bankRecords,
            });
          }

          onSuccess?.();
        }}
      />

      <SubmitBankRecordsCsvImportModal
        closeCsvImportModal={closeModal}
        success={success}
        setSuccess={setSuccess}
      />
    </>
  );
};

export const SubmitBankRecordsCsvImportModal = ({
  closeCsvImportModal,
  success,
  setSuccess,
}: {
  closeCsvImportModal: () => void;
  success: {
    uploadedRecords: typeof TEMPLATES.bankRecords;
  } | null;
  setSuccess: (
    success: {
      uploadedRecords: typeof TEMPLATES.bankRecords;
    } | null
  ) => void;
}) => {
  const [, setForced] = useBankImportCsvForcedParam();

  const accountId = useBankAccountId();

  const theme = useMantineTheme();

  const [{ currency }] = useTeam();

  const skip = !success || !accountId;
  const queryData = useApiQuery(
    async (api, signal, args) => {
      if (!args.accountId) return null;

      const result = await api.GET('/accounts/{id}', {
        params: {
          path: {
            id: args.accountId,
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      return result.data?.banking?.balance?.ending ?? 0;
    },
    {
      queryGroup: 'accounts',
      uniqueQueryKey: 'csv-import-bank-records',
      variables: {
        accountId,
      },
      queryKeys: ['accounts', 'csv-import'],
      skip,
      keepPreviousData: true,
      errorTitle: 'Failed to load account',
    }
  );

  return (
    <Modal
      opened={!!success}
      onClose={() => setSuccess(null)}
      sx={{
        textAlign: 'center',
      }}
      centered
      radius="lg"
    >
      {queryData.isLoading || !queryData.data ? (
        <Center mih={400}>
          <LoadingIndicator size="sm" />
        </Center>
      ) : queryData.error ? (
        <>
          <Center mb="sm">
            <Icon
              icon="CrossCircleIcon"
              color={(theme) => theme.colors.red[6]}
              size={30}
            />
          </Center>
          <Title order={3} fw={500}>
            We failed to load your data.
          </Title>
          <Text c="gray">{queryData.error.message}</Text>
        </>
      ) : (
        <>
          <Center mb="sm">
            <Icon
              icon="CheckCircleIcon"
              color={(theme) => theme.colors.green[6]}
              size={30}
            />
          </Center>
          <Title order={3} fw={500} mb={rem(8)}>
            CSV Successfully Imported
          </Title>
          <Text c="gray">
            {success?.uploadedRecords.length ?? 0} bank record
            {success?.uploadedRecords.length === 1 ? '' : 's'} with a total of{' '}
            {formatCurrency(sum(success?.uploadedRecords, 'Amount'), currency)}{' '}
            imported.
          </Text>
          <Box
            my="xl"
            sx={(theme) => ({
              boxShadow: theme.shadows.md,
              // zIndex: 100,
              border: '1px solid',
              borderColor: theme.colors.yellow[5],
              backgroundColor: theme.colors.yellow[0],
              padding: `${theme.spacing.sm} ${theme.spacing.sm}`,
              borderRadius: theme.radius.md,
            })}
          >
            <Group mb={rem(8)} gap="xs" justify="space-between">
              <Text size="sm" c={theme.colors.neutral[7]}>
                Previous Bank balance
              </Text>
              <Text>
                {formatCurrency(
                  ((queryData.data ?? 0) +
                    sum(
                      success?.uploadedRecords ?? [],
                      (x) => Number(x.Amount || 0) * 100
                    )) /
                    100,
                  currency
                )}
              </Text>
            </Group>

            <Group gap="xs" justify="space-between">
              <Text size="sm" c={theme.colors.neutral[7]}>
                Total imported today
              </Text>
              <Text>
                {formatCurrency(
                  sum(success?.uploadedRecords, 'Amount'),
                  currency
                )}
              </Text>
            </Group>

            <Divider opacity={0.5} my="xs" />

            <Group gap="xs" justify="space-between">
              <Text size="sm" c={theme.colors.neutral[7]}>
                Current Bank Balance
              </Text>
              <Text fw={500}>
                {formatCurrency(queryData.data ?? 0, currency)}
              </Text>
            </Group>
          </Box>

          <Text mb="xl">
            Based on your csv import and previous bank records, your current
            bank balance should be{' '}
            <Text inherit span fw={500}>
              {formatCurrency(queryData.data ?? 0, currency)}
            </Text>
            .
          </Text>

          <Group justify="end">
            <Button
              variant="primary"
              leftIcon="CheckCircleIcon"
              onClick={() => {
                closeCsvImportModal();
                setForced(null);
                setSuccess(null);
              }}
            >
              Finish upload
            </Button>
          </Group>
        </>
      )}
    </Modal>
  );
};
